/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState } from "react";
import { Grid, IconButton, CircularProgress, Modal, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { OrderContext } from "../../Contexts/OrderContext";
import { Vocabulary } from "../../Utils/Vocabulary";
import { urlEnum } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { deleteData } from "../../Services/deleteData";
import DropzoneComponent from "../GenericComponents/DropzoneComponent";
import styles from "../../Styles/showImages.module.css";
import { toast } from "react-toastify";
import axios from "axios";
import { disableTostify } from "../../Utils/Constants";

export default function OrderPhotos() {
  const orderContext = useContext(OrderContext);
  const [files, setFiles] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const token = localStorage.getItem("access_token");
  const axiosConfig: any = token
    ? {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "cache-control": "no-cache",
        },
      }
    : {};

  useEffect(() => {
    if (!orderContext.order?.photos) return;
    const photos = orderContext.order.photos;

    photos.forEach(async (photo: string) => {
      if (imageUrls.has(photo)) return;
      try {
        const res = await axios.get(
          `${urlEnum.products}/getImage/productsImages/${encodeURIComponent(photo)}`,
          axiosConfig,
        );
        const url = URL.createObjectURL(res.data);
        setImageUrls((prev) => new Map(prev).set(photo, url));
      } catch (err) {
        // ignore fetch errors
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderContext.order?.photos]);

  /**
   * Handle file selection from dropzone
   */
  function handleChangeFiles(newFiles: any) {
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    // Auto-upload when files are selected
    uploadPhotos(updatedFiles);
  }

  /**
   * Upload photos to the order
   */
  async function uploadPhotos(filesToUpload: any[]) {
    if (!orderContext.order?._id || filesToUpload.length === 0) return;

    setLoading(true);
    const formData = new FormData();

    // Append files
    filesToUpload.forEach((file: any) => {
      formData.append("files", file, file.name);
    });

    // Append existing photos
    formData.append("photos", JSON.stringify(orderContext.order.photos || []));

    try {
      const res = await postData(
        `${urlEnum.orders}/photos/${orderContext.order._id}${disableTostify}`,
        formData,
      );
      if (res && res.data.result) {
        orderContext.setOrder(res.data.result);
        toast.success(Vocabulary.updateDataSuccess);
      }
    } catch (err) {
      toast.error(Vocabulary.updateDataFailed);
    } finally {
      setFiles([]);
      setLoading(false);
    }
  }

  /**
   * Delete a photo
   */
  async function deletePhoto(photoName: string) {
    if (!orderContext.order?._id) return;

    setLoading(true);
    try {
      const res = await deleteData(
        `${urlEnum.orders}/photos/${orderContext.order._id}/${encodeURIComponent(photoName)}`,
      );
      if (res && res.data.result) {
        // Revoke the blob URL
        const blobUrl = imageUrls.get(photoName);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setImageUrls((prev) => {
          const next = new Map(prev);
          next.delete(photoName);
          return next;
        });
        orderContext.setOrder(res.data.result);
        toast.success(Vocabulary.deleteDataSuccess);
      }
    } catch (err) {
      toast.error(Vocabulary.deleteDataFailed);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Open modal to view photo
   */
  function openPhotoModal(index: number) {
    setCurrentPhotoIndex(index);
    setModalOpen(true);
  }

  /**
   * Close modal
   */
  function closeModal() {
    setModalOpen(false);
  }

  /**
   * Navigate to previous photo
   */
  function goToPreviousPhoto() {
    const photos = orderContext.order?.photos;
    if (!photos) return;
    setCurrentPhotoIndex((prev) =>
      prev > 0 ? prev - 1 : photos.length - 1
    );
  }

  /**
   * Navigate to next photo
   */
  function goToNextPhoto() {
    const photos = orderContext.order?.photos;
    if (!photos) return;
    setCurrentPhotoIndex((prev) =>
      prev < photos.length - 1 ? prev + 1 : 0
    );
  }

  /**
   * Handle keyboard navigation
   */
  useEffect(() => {
    if (!modalOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeModal();
      } else if (event.key === "ArrowLeft") {
        goToPreviousPhoto();
      } else if (event.key === "ArrowRight") {
        goToNextPhoto();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  return (
    <div>
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 10 }}>
          <CircularProgress size={24} />
        </div>
      )}

      {/* Dropzone for uploading */}
      {orderContext.order?._id && (
        <div style={{ marginBottom: 15 }}>
          <DropzoneComponent
            onSave={handleChangeFiles}
            maxFiles={10}
            hidePreview
          />
        </div>
      )}

      {/* Display existing photos */}
      {orderContext.order?.photos && orderContext.order.photos.length > 0 && (
        <Grid container spacing={1}>
          {orderContext.order.photos.map((photo: string, index: number) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <div
                className={styles.displayImagesSmall}
                style={{ position: "relative" }}
              >
                <IconButton
                  className={styles.deleteImagesBtn}
                  onClick={() => deletePhoto(photo)}
                  disabled={loading}
                  size="small"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.8)",
                  }}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
                {imageUrls.get(photo) ? (
                  <img
                    src={imageUrls.get(photo)}
                    alt={`Order photo ${index + 1}`}
                    onClick={() => openPhotoModal(index)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Photo viewer modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        aria-labelledby="photo-modal"
        aria-describedby="photo-viewer-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header with close button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
              borderBottom: "1px solid #ddd",
            }}
          >
            <Box sx={{ fontSize: 14, color: "text.secondary" }}>
              {currentPhotoIndex + 1} / {orderContext.order?.photos?.length || 0}
            </Box>
            <IconButton onClick={closeModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Image container */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              bgcolor: "#000",
              overflow: "hidden",
            }}
          >
            {orderContext.order?.photos && orderContext.order.photos[currentPhotoIndex] && imageUrls.get(orderContext.order.photos[currentPhotoIndex]) ? (
              <img
                src={imageUrls.get(orderContext.order.photos[currentPhotoIndex])}
                alt={`Photo ${currentPhotoIndex + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <CircularProgress />
            )}

            {/* Navigation buttons */}
            {orderContext.order?.photos && orderContext.order.photos.length > 1 && (
              <>
                <IconButton
                  onClick={goToPreviousPhoto}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                <IconButton
                  onClick={goToNextPhoto}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
